// src/context/ProcessingContext.tsx

import React, { createContext, useContext, useReducer, useCallback } from 'react';
import { GeographicMetadata } from '../types/geographic';
import { FeatureCollection } from 'geojson';

// Enhanced types
export interface RawData {
  data: number[][];
  plot: string;
  stats: {
    num_points: number;
    z_min: number;
    z_max: number;
    z_mean: number;
    z_std: number;
  };
  geographic_metadata: GeographicMetadata;
}

export interface GriddedData {
  grid: number[][];
  plot: string;
  stats: {
    valid_cells: number;
    total_cells: number;
    coverage: number;
    cell_size_meters: number;
    grid_dimensions: string;
    min: number;
    max: number;
    mean: number;
    std: number;
  };
  dimensions: {
    width: number;
    height: number;
  };
  metadata: {
    resolution: number;
    coordinate_system: 'utm' | 'projected';
    units: string;
    cell_size_meters: number;
    original_points: number;
    geographic_metadata: GeographicMetadata;
  };
}

export interface InterpolationResult {
  data: number[][];
  plot: string;
  histogram: string;
  stats: {
    min: number;
    max: number;
    mean: number;
    median: number;
    std: number;
    count: number;
  };
  original_points?: number[][];
}

export interface InterpolatedData {
  ml?: InterpolationResult;
  idw?: InterpolationResult;
  rbf?: InterpolationResult;
}

export interface MapData {
  contours: FeatureCollection;
  points?: FeatureCollection;
  bounds?: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

export type ProcessingStep = 'upload' | 'grid' | 'interpolate' | 'contour' | 'map' | 'export';

export interface ProcessingState {
  // Data states
  rawData: RawData | null;
  griddedData: GriddedData | null;
  interpolatedData: InterpolatedData | null;
  contourGeoJSON: GeoJSONData | null;

  // Add this new interface for contour configuration
  contourConfig: {
    interval: number;
    colorScheme: string;
    opacity: number;
    thickness: number;
    labels: boolean;
    showPoints: boolean;
  } | null;

  // View and navigation state
  currentViewStep: ProcessingStep;
  
  // Processing state
  isProcessing: boolean;
  error: string | null;

  // Map state
  mapData: MapData | null;
  mapSettings: {
    baseMap: 'osm' | 'satellite' | 'terrain';
    contourOpacity: number;
    showPoints: boolean;
    showLabels: boolean;
  };
}

export interface GeoJSONFeature {
 type: 'Feature';
 geometry: {
   type: 'LineString';
   coordinates: [number, number][];
 };
 properties: {
   elevation: number;
 };
}

export interface GeoJSONData {
 type: 'FeatureCollection';
 features: GeoJSONFeature[];
}

export type ProcessingAction =
  | { type: 'SET_RAW_DATA'; payload: RawData }
  | { type: 'SET_GRIDDED_DATA'; payload: GriddedData }
  | { type: 'SET_INTERPOLATED_DATA'; payload: InterpolatedData }
  | { type: 'SET_VIEW_STEP'; payload: ProcessingStep }
  | { type: 'SET_PROCESSING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_MAP_DATA'; payload: MapData }
  | { type: 'UPDATE_MAP_SETTINGS'; payload: Partial<ProcessingState['mapSettings']> }
  | { type: 'SET_CONTOUR_GEOJSON'; payload: GeoJSONData }
  | { type: 'UPDATE_CONTOUR_CONFIG'; payload: Partial<NonNullable<ProcessingState['contourConfig']>> }

const initialState: ProcessingState = {
  rawData: null,
  griddedData: null,
  interpolatedData: null,
  currentViewStep: 'upload',
  isProcessing: false,
  error: null,
  contourGeoJSON: null,
  contourConfig: {
    interval: 1,
    colorScheme: 'terrain',
    opacity: 0.7,
    thickness: 0.5,
    labels: true,
    showPoints: false
  },
  mapData: null,
  mapSettings: {
    baseMap: 'osm',
    contourOpacity: 0.7,
    showPoints: true,
    showLabels: true
  }
};

const processingReducer = (state: ProcessingState, action: ProcessingAction): ProcessingState => {
  switch (action.type) {
    case 'SET_RAW_DATA':
      return {
        ...state,
        rawData: action.payload,
        error: null
      };

    case 'SET_GRIDDED_DATA':
      return {
        ...state,
        griddedData: action.payload,
        error: null
      };

    case 'SET_INTERPOLATED_DATA':
      return {
        ...state,
        interpolatedData: action.payload,
        error: null
      };

    case 'SET_VIEW_STEP':
      return {
        ...state,
        currentViewStep: action.payload,
        error: null
      };

    case 'SET_PROCESSING':
      return {
        ...state,
        isProcessing: action.payload
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
        isProcessing: false
      };

    case 'SET_MAP_DATA':
      return {
        ...state,
        mapData: action.payload,
        error: null
      };

    case 'UPDATE_MAP_SETTINGS':
      return {
        ...state,
        mapSettings: {
          ...state.mapSettings,
          ...action.payload
        }
      };
    
    case 'SET_CONTOUR_GEOJSON':
      return {
        ...state,
        contourGeoJSON: action.payload
      };
    
    case 'UPDATE_CONTOUR_CONFIG':
      return {
        ...state,
        contourConfig: {
          ...state.contourConfig,
          ...action.payload
        }
      };

    default:
      return state;
  }
};

export const ProcessingContext = createContext<{
  state: ProcessingState;
  dispatch: React.Dispatch<ProcessingAction>;
}>({
  state: initialState,
  dispatch: () => undefined,
});

export const ProcessingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(processingReducer, initialState);

  return (
    <ProcessingContext.Provider value={{ state, dispatch }}>
      {children}
    </ProcessingContext.Provider>
  );
};

export const useProcessing = () => {
  const context = useContext(ProcessingContext);
  if (!context) {
    throw new Error('useProcessing must be used within a ProcessingProvider');
  }
  return context;
};

export const useMapOperations = () => {
  const { state, dispatch } = useProcessing();

  const updateMapSettings = useCallback((settings: Partial<ProcessingState['mapSettings']>) => {
    dispatch({ type: 'UPDATE_MAP_SETTINGS', payload: settings });
  }, [dispatch]);

  const setMapData = useCallback((data: MapData) => {
    dispatch({ type: 'SET_MAP_DATA', payload: data });
  }, [dispatch]);

  return {
    mapData: state.mapData,
    mapSettings: state.mapSettings,
    updateMapSettings,
    setMapData
  };
};

// Utility function for step navigation
export const canNavigateToStep = (state: ProcessingState, step: ProcessingStep): boolean => {
  const steps: ProcessingStep[] = ['upload', 'grid', 'interpolate', 'contour', 'map', 'export'];
  const currentIdx = steps.indexOf(state.currentViewStep);
  const targetIdx = steps.indexOf(step);

  // Can always go backwards
  if (targetIdx <= currentIdx) return true;

  // Check if previous step has data
  switch (step) {
    case 'grid':
      return !!state.rawData;
    case 'interpolate':
      return !!state.griddedData;
    case 'contour':
      return !!state.interpolatedData;
    case 'map':
      return !!state.interpolatedData;
    case 'export':
      return !!state.interpolatedData;
    default:
      return true;
  }
};